import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Group,
  LineBasicMaterial,
  LineSegments,
  PerspectiveCamera,
  Points,
  Scene,
  ShaderMaterial,
  Vector2,
  WebGLRenderer,
} from "three";

const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
const coarsePointer = window.matchMedia("(pointer: coarse)");

const clamp = (value, minimum = 0, maximum = 1) => {
  return Math.min(Math.max(value, minimum), maximum);
};

const createRandom = (seed = 20260713) => {
  let state = seed >>> 0;

  return () => {
    state += 0x6d2b79f5;
    let result = state;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
};

const particleProfiles = {
  nebula: {
    scale: 1,
    seed: 20260713,
    distribution: "spiral",
    drift: 0,
    opacity: 0.82,
    violet: "#8d73ff",
    cyan: "#66f6ff",
    white: "#fbfbff",
    line: "#66f6ff",
    cameraZ: 8.2,
  },
  campus: {
    scale: 0.58,
    seed: 20260909,
    distribution: "field",
    drift: 1,
    opacity: 0.76,
    violet: "#ff9a4f",
    cyan: "#ffd36a",
    white: "#fff4d6",
    line: "#ffd36a",
    cameraZ: 8.8,
  },
  pagoda: {
    scale: 0.68,
    seed: 20261008,
    distribution: "field",
    drift: 0.72,
    opacity: 0.7,
    violet: "#315bff",
    cyan: "#66f6ff",
    white: "#ffd89b",
    line: "#66f6ff",
    cameraZ: 9,
  },
  ambient: {
    scale: 0.42,
    seed: 20261117,
    distribution: "spiral",
    drift: 0.35,
    opacity: 0.46,
    violet: "#6847ff",
    cyan: "#d049ff",
    white: "#fbfbff",
    line: "#8d73ff",
    cameraZ: 9.4,
  },
};

const getProfile = (profile) =>
  particleProfiles[profile] ?? particleProfiles.nebula;

const getQuality = (profile = "nebula") => {
  const memory = Number(navigator.deviceMemory ?? 8);
  const cores = Number(navigator.hardwareConcurrency ?? 8);
  const mobile = window.matchMedia("(max-width: 48rem)").matches;
  const constrained = memory <= 4 || cores <= 4;

  const profileScale = getProfile(profile).scale;
  const scaleCount = (count) => Math.max(Math.round(count * profileScale), 120);

  if (mobile || constrained) {
    return {
      count: scaleCount(mobile ? 220 : 320),
      dpr: 1,
      frameInterval: 1000 / 30,
      powerPreference: "low-power",
    };
  }

  if (window.matchMedia("(max-width: 75rem)").matches) {
    return {
      count: scaleCount(420),
      dpr: 1,
      frameInterval: 1000 / 40,
      powerPreference: "high-performance",
    };
  }

  return {
    count: scaleCount(760),
    dpr: Math.min(window.devicePixelRatio || 1, 1.5),
    frameInterval: 1000 / 60,
    powerPreference: "high-performance",
  };
};

const createGeometry = (count, profile = "nebula") => {
  const settings = getProfile(profile);
  const fieldDistribution = settings.distribution === "field";
  const random = createRandom(settings.seed);
  const positions = new Float32Array(count * 3);
  const scales = new Float32Array(count);
  const phases = new Float32Array(count);
  const colorMixes = new Float32Array(count);

  for (let index = 0; index < count; index += 1) {
    const positionIndex = index * 3;

    if (fieldDistribution) {
      const depth = (random() - 0.5) * 6.4;

      positions[positionIndex] = (random() - 0.5) * 11.5 + depth * 0.16;
      positions[positionIndex + 1] = (random() - 0.5) * 6.8;
      positions[positionIndex + 2] = depth;
      scales[index] = 0.72 + Math.pow(random(), 2) * 2.35;
      phases[index] = random() * Math.PI * 2;
      colorMixes[index] = clamp(0.1 + random() * 0.9);
      continue;
    }

    const arm = index % 3;
    const radius = 0.45 + Math.pow(random(), 0.72) * 7.2;
    const angle =
      radius * 0.72 + arm * ((Math.PI * 2) / 3) + (random() - 0.5) * 0.68;
    positions[positionIndex] =
      Math.cos(angle) * radius * 1.36 + (random() - 0.5) * 0.5;
    positions[positionIndex + 1] =
      Math.sin(angle) * radius * 0.58 + (random() - 0.5) * 0.75;
    positions[positionIndex + 2] = (random() - 0.5) * 3.8 - radius * 0.08;
    scales[index] = 0.55 + Math.pow(random(), 2.2) * 1.8;
    phases[index] = random() * Math.PI * 2;
    colorMixes[index] = clamp(radius / 7.65 + (random() - 0.5) * 0.24);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  geometry.setAttribute("aScale", new BufferAttribute(scales, 1));
  geometry.setAttribute("aPhase", new BufferAttribute(phases, 1));
  geometry.setAttribute("aColorMix", new BufferAttribute(colorMixes, 1));

  return geometry;
};

const createConnections = (geometry, profile = "nebula") => {
  const position = geometry.getAttribute("position");
  const settings = getProfile(profile);
  const linePositions = [];
  const stride = Math.max(Math.floor(position.count / 72), 1);
  const threshold = settings.distribution === "field" ? 2.5 : 2.15;

  for (
    let index = 0;
    index + stride < position.count;
    index += stride
  ) {
    const next = index + stride;
    const x1 = position.getX(index);
    const y1 = position.getY(index);
    const z1 = position.getZ(index);
    const x2 = position.getX(next);
    const y2 = position.getY(next);
    const z2 = position.getZ(next);
    const distance = Math.hypot(x2 - x1, y2 - y1, z2 - z1);

    if (distance <= threshold) {
      linePositions.push(x1, y1, z1, x2, y2, z2);
    }
  }

  const lineGeometry = new BufferGeometry();
  lineGeometry.setAttribute(
    "position",
    new BufferAttribute(new Float32Array(linePositions), 3),
  );

  const lineMaterial = new LineBasicMaterial({
    color: new Color(settings.line),
    transparent: true,
    opacity: profile === "ambient" ? 0.05 : 0.1,
    depthWrite: false,
    blending: AdditiveBlending,
  });

  return {
    lines: new LineSegments(lineGeometry, lineMaterial),
    geometry: lineGeometry,
    material: lineMaterial,
  };
};

const vertexShader = `
  attribute float aScale;
  attribute float aPhase;
  attribute float aColorMix;

  uniform float uTime;
  uniform float uPixelRatio;
  uniform float uScroll;
  uniform float uDrift;
  uniform float uImpulse;
  uniform vec2 uPointer;

  varying float vPhase;
  varying float vColorMix;
  varying float vDepth;

  void main() {
    vec3 transformed = position;
    float depthFactor = smoothstep(-3.0, 2.0, transformed.z);
    float pulse = sin(uTime * 0.42 + aPhase + transformed.x * 0.16);

    transformed.x += uPointer.x * mix(0.08, 0.52, depthFactor);
    transformed.y += uPointer.y * mix(0.06, 0.38, depthFactor);
    transformed.y += pulse * 0.075;
    transformed.x += sin(uTime * 0.16 + aPhase) * 0.055;
    transformed.x += uDrift * cos(uTime * 0.2 + aPhase * 1.7) * 0.18;
    transformed.y += uDrift * sin(uTime * 0.26 + aPhase * 1.3) * 0.24;
    transformed.z += uDrift * sin(uTime * 0.18 + aPhase) * 0.16;
    transformed.z += uScroll * (0.22 + depthFactor * 0.52);
    vec2 burstOrigin = vec2(uPointer.x * 3.4, uPointer.y * 2.1);
    vec3 burstDirection = normalize(
      vec3(transformed.xy - burstOrigin + vec2(0.001), transformed.z * 0.18 + 0.12)
    );
    transformed += burstDirection * uImpulse * mix(0.16, 0.78, depthFactor);

    vec4 modelPosition = modelMatrix * vec4(transformed, 1.0);
    vec4 viewPosition = viewMatrix * modelPosition;
    vec4 projectedPosition = projectionMatrix * viewPosition;

    gl_Position = projectedPosition;
    gl_PointSize = aScale * uPixelRatio * (22.0 / max(-viewPosition.z, 0.1));

    vPhase = aPhase;
    vColorMix = aColorMix;
    vDepth = depthFactor;
  }
`;

const fragmentShader = `
  uniform float uTime;
  uniform float uOpacity;
  uniform vec3 uViolet;
  uniform vec3 uCyan;
  uniform vec3 uWhite;

  varying float vPhase;
  varying float vColorMix;
  varying float vDepth;

  void main() {
    float distanceToCenter = length(gl_PointCoord - vec2(0.5));
    float softDisc = smoothstep(0.5, 0.06, distanceToCenter);
    float core = smoothstep(0.18, 0.0, distanceToCenter);
    float twinkle = 0.68 + sin(uTime * 0.9 + vPhase) * 0.2;
    vec3 spectral = mix(uViolet, uCyan, vColorMix);
    vec3 color = mix(spectral, uWhite, core * 0.72);
    float alpha = softDisc * twinkle * mix(0.38, 0.92, vDepth) * uOpacity;

    if (alpha < 0.01) discard;
    gl_FragColor = vec4(color, alpha);
  }
`;

const createWebGLField = (mount, initialScrollProgress = 0) => {
  const profile = mount.dataset.particleProfile ?? "nebula";
  const settings = getProfile(profile);
  const fieldDistribution = settings.distribution === "field";
  const quality = getQuality(profile);
  const scene = new Scene();
  const camera = new PerspectiveCamera(54, 1, 0.1, 40);
  const group = new Group();
  const geometry = createGeometry(quality.count, profile);
  const connection = createConnections(geometry, profile);
  const pointer = {
    target: new Vector2(),
    current: new Vector2(),
  };
  const scroll = {
    target: clamp(initialScrollProgress),
    current: clamp(initialScrollProgress),
  };
  const impulse = {
    target: 0,
    current: 0,
  };
  const uniforms = {
    uTime: { value: 0 },
    uPixelRatio: { value: quality.dpr },
    uScroll: { value: scroll.current },
    uDrift: { value: settings.drift },
    uImpulse: { value: 0 },
    uPointer: { value: pointer.current },
    uOpacity: { value: settings.opacity },
    uViolet: { value: new Color(settings.violet) },
    uCyan: { value: new Color(settings.cyan) },
    uWhite: { value: new Color(settings.white) },
  };
  const material = new ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    blending: AdditiveBlending,
  });
  const points = new Points(geometry, material);
  const renderer = new WebGLRenderer({
    alpha: true,
    antialias: false,
    powerPreference: quality.powerPreference,
    premultipliedAlpha: true,
  });

  let inView = true;
  let pageVisible = !document.hidden;
  let motionReduced = motionPreference.matches;
  let running = false;
  let destroyed = false;
  let lastFrame = 0;

  camera.position.set(0, 0, settings.cameraZ);
  group.rotation.z = fieldDistribution ? -0.025 : -0.11;
  group.add(connection.lines, points);
  scene.add(group);

  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(quality.dpr);
  renderer.domElement.className = "particle-canvas hero__particle-canvas";
  renderer.domElement.setAttribute("aria-hidden", "true");
  mount.replaceChildren(renderer.domElement);

  const renderFrame = (time = performance.now(), force = false) => {
    if (destroyed) {
      return;
    }

    if (!force && time - lastFrame < quality.frameInterval) {
      return;
    }

    const delta = Math.min(Math.max((time - lastFrame) / 16.67, 0.1), 3);
    lastFrame = time;
    pointer.current.lerp(pointer.target, 1 - Math.pow(0.9, delta));
    scroll.current +=
      (scroll.target - scroll.current) * (1 - Math.pow(0.93, delta));
    impulse.current +=
      (impulse.target - impulse.current) * (1 - Math.pow(0.72, delta));
    impulse.target *= Math.pow(0.9, delta);

    uniforms.uTime.value = time * 0.001;
    uniforms.uScroll.value = scroll.current;
    uniforms.uImpulse.value = impulse.current;
    uniforms.uOpacity.value =
      profile === "nebula"
        ? settings.opacity * (1 - scroll.current * 0.78)
        : settings.opacity;
    connection.material.opacity =
      (profile === "ambient" ? 0.05 : 0.1) + impulse.current * 0.08;
    group.rotation.z = fieldDistribution
      ? -0.025 + Math.sin(time * 0.00012) * 0.025
      : -0.11 + scroll.current * 0.18;
    group.rotation.y = pointer.current.x * (fieldDistribution ? 0.13 : 0.035);
    group.rotation.x = -pointer.current.y * (fieldDistribution ? 0.075 : 0.018);
    group.position.y = fieldDistribution
      ? Math.sin(time * 0.00018) * 0.08
      : scroll.current * 0.52;
    camera.position.x = pointer.current.x * (fieldDistribution ? 0.32 : 0.13);
    camera.position.y = -pointer.current.y * (fieldDistribution ? 0.24 : 0.1);

    renderer.render(scene, camera);
  };

  const syncLoop = () => {
    const shouldRun = inView && pageVisible && !motionReduced && !destroyed;

    if (shouldRun === running) {
      return;
    }

    running = shouldRun;
    renderer.setAnimationLoop(shouldRun ? renderFrame : null);

    if (!shouldRun && !destroyed) {
      renderFrame(performance.now(), true);
    }
  };

  const resize = () => {
    const rect = mount.getBoundingClientRect();
    const width = Math.max(Math.round(rect.width), 1);
    const height = Math.max(Math.round(rect.height), 1);

    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
    renderFrame(performance.now(), true);
  };

  const updatePointer = (event) => {
    const rect = mount.getBoundingClientRect();
    pointer.target.set(
      clamp((event.clientX - rect.left) / rect.width, 0, 1) * 2 - 1,
      -(clamp((event.clientY - rect.top) / rect.height, 0, 1) * 2 - 1),
    );
  };

  const resetPointer = () => {
    pointer.target.set(0, 0);
  };

  const triggerImpulse = (event) => {
    if (Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
      updatePointer(event);
    }

    impulse.current = Math.max(impulse.current, 0.32);
    impulse.target = 1.16;
  };

  const handleVisibility = () => {
    pageVisible = !document.hidden;
    syncLoop();
  };

  const handleMotionPreference = (event) => {
    motionReduced = event.matches;
    mount.classList.toggle("is-static", motionReduced);
    syncLoop();
  };

  const handleContextLoss = (event) => {
    event.preventDefault();
    mount.classList.add("is-fallback");
    inView = false;
    syncLoop();
  };

  const resizeObserver = new ResizeObserver(resize);
  const visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      inView = Boolean(entry?.isIntersecting);
      syncLoop();
    },
    { threshold: 0.01 },
  );

  const interactionSurface = mount.closest(
    ".hero, .page-hero, .archive-shot, .light-study, .page-outro",
  );

  if (!coarsePointer.matches) {
    interactionSurface?.addEventListener("pointermove", updatePointer, {
      passive: true,
    });
    interactionSurface?.addEventListener("pointerleave", resetPointer);
  }

  interactionSurface?.addEventListener("pointerdown", triggerImpulse, {
    passive: true,
  });

  document.addEventListener("visibilitychange", handleVisibility);
  motionPreference.addEventListener("change", handleMotionPreference);
  renderer.domElement.addEventListener("webglcontextlost", handleContextLoss);
  resizeObserver.observe(mount);
  visibilityObserver.observe(mount);
  resize();
  syncLoop();

  return {
    setScrollProgress(progress) {
      scroll.target = clamp(progress);
    },
    destroy() {
      if (destroyed) {
        return;
      }

      destroyed = true;
      renderer.setAnimationLoop(null);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
      document.removeEventListener("visibilitychange", handleVisibility);
      motionPreference.removeEventListener("change", handleMotionPreference);
      interactionSurface?.removeEventListener("pointermove", updatePointer);
      interactionSurface?.removeEventListener("pointerleave", resetPointer);
      interactionSurface?.removeEventListener("pointerdown", triggerImpulse);
      renderer.domElement.removeEventListener(
        "webglcontextlost",
        handleContextLoss,
      );
      geometry.dispose();
      connection.geometry.dispose();
      connection.material.dispose();
      material.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      renderer.domElement.remove();
    },
  };
};

export const initNebulaParticles = () => {
  const mounts = [...document.querySelectorAll("[data-nebula-field]")];
  const saveData = Boolean(navigator.connection?.saveData);
  const entries = mounts.map((mount) => ({
    mount,
    profile: mount.dataset.particleProfile ?? "nebula",
    field: null,
  }));
  let scrollProgress = 0;
  let destroyed = false;

  if (entries.length === 0) {
    return null;
  }

  const setup = () => {
    entries.forEach((entry) => {
      entry.field?.destroy();
      entry.field = null;
      entry.mount.classList.remove("is-ready", "is-fallback", "is-static");

      if (
        destroyed ||
        motionPreference.matches ||
        saveData ||
        !("WebGL2RenderingContext" in window)
      ) {
        entry.mount.classList.add("is-static");
        return;
      }

      try {
        const initialProgress =
          entry.profile === "nebula" ? scrollProgress : 0;
        entry.field = createWebGLField(entry.mount, initialProgress);
        entry.mount.classList.add("is-ready");
      } catch (error) {
        console.warn(
          `${entry.profile} particle field is using the static fallback`,
          error,
        );
        entry.mount.classList.add("is-fallback");
      }
    });
  };

  const handlePreferenceChange = () => setup();
  const handlePageHide = (event) => {
    if (!event.persisted) {
      controller.destroy();
    }
  };

  const controller = {
    setScrollProgress(progress) {
      scrollProgress = clamp(progress);
      entries.forEach((entry) => {
        if (entry.profile === "nebula") {
          entry.field?.setScrollProgress(scrollProgress);
        }
      });
    },
    destroy() {
      if (destroyed) {
        return;
      }

      destroyed = true;
      entries.forEach((entry) => {
        entry.field?.destroy();
        entry.field = null;
        entry.mount.replaceChildren();
      });
      motionPreference.removeEventListener("change", handlePreferenceChange);
      window.removeEventListener("pagehide", handlePageHide);
    },
  };

  motionPreference.addEventListener("change", handlePreferenceChange);
  window.addEventListener("pagehide", handlePageHide);
  setup();

  return controller;
};
